// src/components/Upload.js

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload, FaFilePdf, FaTrashAlt, FaVideo } from 'react-icons/fa'; // ← Added FaVideo
import FileUploader from './FileUploader';
import { useAuth } from '../utils/AuthContext';
import '../styles/styles.css';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

const formatFileSize = (bytes) => {
    if (bytes === 0) {
        return '0 KB';
    }
    if (!bytes) {
        return '';
    }
    const megabytes = bytes / (1024 * 1024);
    if (megabytes >= 1) {
        return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
    }
    const kilobytes = bytes / 1024;
    return `${kilobytes.toFixed(0)} KB`;
};

const Upload = ({ onDocumentUpdate = () => { } }) => {
    const { authState, setAuthData } = useAuth();
    const navigate = useNavigate();

    const [file, setFile] = useState(null);
    const [apiStatus, setApiStatus] = useState('');
    const [timer, setTimer] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [isDragActive, setIsDragActive] = useState(false);
    const [generationType, setGenerationType] = useState('quiz'); // Now supports 'video'

    const fileInputRef = useRef(null);

    const safeAuthState = useMemo(() =>
        authState || { accessToken: '', userId: '', userName: '', supabaseRefreshToken: '', userEmail: '' },
        [authState]
    );

    const currentFile = useMemo(() => (file ? file[0] : null), [file]);

    const resetFileInput = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleFiles = useCallback((files) => {
        if (!files?.length) {
            return;
        }
        if (files.length > 1) {
            setValidationError('Please upload only one PDF at a time.');
            setFile(null);
            setApiStatus('');
            setTimer(0);
            resetFileInput();
            return;
        }
        const selectedFile = files[0];
        const isPdf = selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf');
        let errorMessage = '';
        if (!isPdf) {
            errorMessage = 'Only PDF files are supported.';
        } else if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            errorMessage = 'Maximum file size is 25 MB.';
        }
        if (errorMessage) {
            setValidationError(errorMessage);
            setFile(null);
            setApiStatus('');
            setTimer(0);
        } else {
            setValidationError('');
            setApiStatus('');
            setTimer(0);
            setFile([selectedFile]);
        }
        resetFileInput();
    }, [resetFileInput, setApiStatus, setTimer]);

    const handleFileInputChange = useCallback((event) => {
        handleFiles(event.target.files);
    }, [handleFiles]);

    const triggerFileDialog = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleDragEnter = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    }, []);

    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isDragActive) {
            setIsDragActive(true);
        }
    }, [isDragActive]);

    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.currentTarget.contains(event.relatedTarget)) {
            return;
        }
        setIsDragActive(false);
    }, []);

    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        const files = event.dataTransfer?.files;
        if (files?.length) {
            handleFiles(files);
        }
    }, [handleFiles]);

    const handleDropzoneKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFileDialog();
        }
    }, [triggerFileDialog]);

    const clearSelection = useCallback(() => {
        setFile(null);
        setValidationError('');
        setApiStatus('');
        setTimer(0);
        resetFileInput();
    }, [resetFileInput, setApiStatus, setTimer]);

    const startTimer = () => {
        setTimer(0);
        const interval = setInterval(() => {
            setTimer((prev) => prev + 1);
        }, 1000);
        return interval;
    };

    const stopTimer = (interval) => {
        clearInterval(interval);
    };

    const handleSignIn = useCallback((userData) => {
        const accessTokenToUse = userData.accessToken;
        const refreshTokenToUse = userData.refreshToken;
        if (!accessTokenToUse) {
            console.error('[Upload] Sign-in failed: No access token');
            setIsUploading(false);
            return;
        }
        const derivedUserName = userData.firstname && userData.lastname
            ? `${userData.firstname} ${userData.lastname}`
            : safeAuthState.userName || '';
        const derivedEmail = userData.email || safeAuthState.userEmail || '';
        const derivedUserId = userData.user_id || safeAuthState.userId || '';
        setAuthData(
            derivedUserId,
            derivedUserName,
            accessTokenToUse,
            refreshTokenToUse,
            derivedEmail,
            () => {
                setIsUploading(false);
            }
        );
    }, [safeAuthState, setAuthData]);

    const refreshDocuments = useCallback((newDoc) => {
        onDocumentUpdate(newDoc);
        setFile(null);
        setValidationError('');
        // Navigate based on generation type
        if (generationType === 'flashcards') {
            navigate('/flashcard-list', { state: { selectedDocument: newDoc } });
        } else if (generationType === 'podcast') {
            navigate('/podcast-list', { state: { selectedDocument: newDoc } });
        } else if (generationType === 'video') {
            navigate('/video-list', { state: { selectedDocument: newDoc } });
        } else {
            navigate('/quiz-list', { state: { selectedDocument: newDoc } });
        }
    }, [onDocumentUpdate, navigate, generationType]);

    return (
        <div className="content-wrapper">
            <div className="upload-page">
                <div
                    className={`upload-card ${isUploading ? 'upload-card--disabled' : ''}`}
                    aria-live="polite"
                >
                    <header className="upload-card__header">
                        <h1 className="upload-card__title">Upload a PDF to generate content</h1>
                        <p className="upload-card__subtitle">
                            We currently support PDF files up to 25 MB. Drag and drop your course material or select it from your device.
                        </p>
                    </header>
                    <div
                        className={`upload-dropzone ${isDragActive ? 'upload-dropzone--active' : ''}`}
                        onKeyDown={handleDropzoneKeyDown}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        role="button"
                        tabIndex={0}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            multiple={false}
                            onChange={handleFileInputChange}
                            aria-label="Select a PDF file to upload"
                        />
                        <FaUpload size={28} className="upload-dropzone__icon" />
                        <div className="upload-dropzone__copy">
                            <span className="upload-dropzone__primary">Drag &amp; drop your PDF here</span>
                            <span className="upload-dropzone__secondary">or click to browse your files</span>
                            <span className="upload-dropzone__hint">PDF only - Max 25 MB</span>
                        </div>
                    </div>
                    {validationError && (
                        <div className="upload-feedback upload-feedback--error">
                            {validationError}
                        </div>
                    )}
                    {currentFile && (
                        <div className="upload-file-summary">
                            <div className="upload-file-summary__meta">
                                <FaFilePdf size={20} className="upload-file-summary__icon" />
                                <div>
                                    <p className="upload-file-name">{currentFile.name}</p>
                                    <p className="upload-file-size">{formatFileSize(currentFile.size)}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="button button--ghost upload-file-summary__remove"
                                onClick={clearSelection}
                                disabled={isUploading}
                                aria-label="Remove file"
                            >
                                <FaTrashAlt size={18} className="upload-file-summary__remove-icon" />
                            </button>
                        </div>
                    )}
                    {currentFile && !isUploading && (
                        <div className="generation-type-selector">
                            <h3 className="generation-type-selector__title">What would you like to generate?</h3>
                            <div className="generation-type-selector__options">
                                <button
                                    type="button"
                                    className={`generation-type-option ${generationType === 'quiz' ? 'generation-type-option--active' : ''}`}
                                    onClick={() => setGenerationType('quiz')}
                                    disabled={isUploading}
                                >
                                    <div className="generation-type-option__content">
                                        <h4>Quiz</h4>
                                        <p>Interactive questions to test your knowledge</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className={`generation-type-option ${generationType === 'flashcards' ? 'generation-type-option--active' : ''}`}
                                    onClick={() => setGenerationType('flashcards')}
                                    disabled={isUploading}
                                >
                                    <div className="generation-type-option__content">
                                        <h4>Flashcards</h4>
                                        <p>Study cards with questions and answers</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className={`generation-type-option ${generationType === 'podcast' ? 'generation-type-option--active' : ''}`}
                                    onClick={() => setGenerationType('podcast')}
                                    disabled={isUploading}
                                >
                                    <div className="generation-type-option__content">
                                        <h4>Podcast</h4>
                                        <p>AI-generated audio content from your document</p>
                                    </div>
                                </button>
                                {/* NEW: Video Option - matches original style perfectly */}
                                <button
                                    type="button"
                                    className={`generation-type-option ${generationType === 'video' ? 'generation-type-option--active' : ''}`}
                                    onClick={() => setGenerationType('video')}
                                    disabled={isUploading}
                                >
                                    <div className="generation-type-option__content">
                                        <FaVideo size={32} style={{ marginBottom: '8px', color: '#4a90e2' }} />
                                        <h4>Video</h4>
                                        <p>AI-generated educational video from your document</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="upload-actions">
                        {isUploading && (
                            <div className="upload-actions__status" role="status" aria-live="polite">
                                <div className="spinner spinner--inline" aria-hidden="true"></div>
                                <span className="upload-actions__status-text">
                                    {apiStatus || 'Processing your document... This may take a moment.'}
                                </span>
                            </div>
                        )}
                        {apiStatus && !isUploading && (
                            <div
                                className={`upload-feedback ${apiStatus.includes('completed') || apiStatus.includes('Completed') ? 'upload-feedback--success' : 'upload-feedback--info'}`}
                                role="status"
                                aria-live="polite"
                            >
                                {apiStatus.includes('completed') || apiStatus.includes('Completed')
                                    ? `${generationType === 'flashcards' ? 'Flashcards' : generationType === 'podcast' ? 'Podcast' : generationType === 'video' ? 'Video' : 'Quiz'} generated in ${timer} seconds.`
                                    : apiStatus}
                            </div>
                        )}
                        <FileUploader
                            file={file}
                            setApiStatus={setApiStatus}
                            startTimer={startTimer}
                            stopTimer={stopTimer}
                            setLoading={setIsUploading}
                            userId={safeAuthState.userId}
                            refreshDocuments={refreshDocuments}
                            accessToken={safeAuthState.accessToken}
                            supabaseRefreshToken={safeAuthState.supabaseRefreshToken || ''}
                            onSignIn={handleSignIn}
                            generationType={generationType}
                            customButton={(onPress) => (
                                <button
                                    type="button"
                                    className="button upload-primary-button"
                                    onClick={onPress}
                                    disabled={!currentFile || isUploading}
                                >
                                    <FaUpload size={18} className="refresh-icon" />
                                    {isUploading
                                        ? 'Processing...'
                                        : `Upload & Generate ${generationType === 'flashcards' ? 'Flashcards' : generationType === 'podcast' ? 'Podcast' : generationType === 'video' ? 'Video' : 'Quiz'}`
                                    }
                                </button>
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Upload;